import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import { 
  TrendingUp, BarChart3, Briefcase, Settings, LogOut, Brain,
  HelpCircle, Bug, Lightbulb, Upload, Send, Clock, CheckCircle2,
  AlertCircle, MessageSquare, Image, Shield, Loader2, FileSpreadsheet, X, Paperclip, Download
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';

import API from '../utils/api';

const SupportPage = () => {
  const { language } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTicketModal, setShowNewTicketModal] = useState(false);
  const [showTicketDetailModal, setShowTicketDetailModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [newComment, setNewComment] = useState('');
  
  // New ticket form with attachments support
  const [ticketForm, setTicketForm] = useState({
    category: 'bug',
    title: '',
    description: '',
    attachments: [] // { type: 'image' | 'csv', filename: string, data: base64, preview?: string }
  });

  const fetchTickets = useCallback(async () => {
    try {
      const response = await fetch(`${API}/tickets`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTickets(data);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTicketDetails = async (ticketId) => {
    try {
      const response = await fetch(`${API}/tickets/${ticketId}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setSelectedTicket(data);
        setShowTicketDetailModal(true);
      }
    } catch (error) {
      console.error('Error fetching ticket details:', error);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        const isImage = file.type.startsWith('image/');
        const isCsv = file.type === 'text/csv' || file.name.endsWith('.csv');
        
        if (isImage || isCsv) {
          const newAttachment = {
            type: isImage ? 'image' : 'csv',
            filename: file.name,
            data: base64,
            preview: isImage ? reader.result : null
          };
          
          setTicketForm(prev => ({
            ...prev,
            attachments: [...prev.attachments, newAttachment]
          }));
        }
      };
      reader.readAsDataURL(file);
    });
    
    // Reset input
    e.target.value = '';
  };

  const removeAttachment = (index) => {
    setTicketForm(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmitTicket = async () => {
    if (!ticketForm.title.trim() || !ticketForm.description.trim()) {
      toast.error(language === 'de' ? 'Bitte Titel und Beschreibung ausfüllen' : 'Please fill in title and description');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          category: ticketForm.category,
          title: ticketForm.title,
          description: ticketForm.description,
          attachments: ticketForm.attachments.map(att => ({
            type: att.type,
            filename: att.filename,
            data: att.data
          }))
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setShowNewTicketModal(false);
        setTicketForm({ category: 'bug', title: '', description: '', attachments: [] });
        fetchTickets();
        toast.success(
          language === 'de' ? 'Ticket erstellt!' : 'Ticket created!',
          {
            description: data.ticket_number,
            duration: 5000,
          }
        );
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Ticket creation failed:', response.status, errorData);
        toast.error(
          language === 'de' ? 'Fehler beim Erstellen des Tickets' : 'Failed to create ticket',
          { description: errorData.detail || `Status: ${response.status}` }
        );
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error(
        language === 'de' ? 'Netzwerkfehler' : 'Network error',
        { description: error.message }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedTicket) return;
    
    try {
      const response = await fetch(`${API}/tickets/${selectedTicket.ticket_id}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ text: newComment })
      });
      
      if (response.ok) {
        setNewComment('');
        fetchTicketDetails(selectedTicket.ticket_id);
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      open: { 
        label: language === 'de' ? 'Offen' : 'Open', 
        className: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
        icon: <AlertCircle className="h-3 w-3" />
      },
      in_progress: { 
        label: language === 'de' ? 'In Bearbeitung' : 'In Progress', 
        className: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
        icon: <Clock className="h-3 w-3" />
      },
      done: { 
        label: language === 'de' ? 'Erledigt' : 'Done', 
        className: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
        icon: <CheckCircle2 className="h-3 w-3" />
      }
    };
    const config = statusConfig[status] || statusConfig.open;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${config.className}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  const getCategoryBadge = (category) => {
    if (category === 'bug') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-rose-500/20 text-rose-400 border border-rose-500/30">
          <Bug className="h-3 w-3" />
          Bug
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400 border border-purple-500/30">
        <Lightbulb className="h-3 w-3" />
        Feature
      </span>
    );
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAttachmentCount = (ticket) => {
    if (ticket.attachments && ticket.attachments.length > 0) {
      return ticket.attachments.length;
    }
    if (ticket.screenshot_url) {
      return 1;
    }
    return 0;
  };

  const downloadAttachment = (attachment) => {
    const link = document.createElement('a');
    link.href = attachment.data_url;
    link.download = attachment.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background flex">
        {/* Sidebar */}
        <Sidebar user={user} onLogout={handleLogout} />

        {/* Main Content */}
        <main className="flex-1 p-6">
          <div className="max-w-5xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-500/20 to-blue-500/20 border border-emerald-500/30">
                  <HelpCircle className="h-8 w-8 text-emerald-400" />
                </div>
                <div>
                  <h1 className="text-3xl font-heading font-bold">Support</h1>
                  <p className="text-muted-foreground">
                    {language === 'de' 
                      ? 'Melden Sie Probleme oder schlagen Sie neue Funktionen vor'
                      : 'Report issues or suggest new features'}
                  </p>
                </div>
              </div>
              <Button onClick={() => setShowNewTicketModal(true)} className="glow-primary" data-testid="new-ticket-btn">
                <Send className="h-4 w-4 mr-2" />
                {language === 'de' ? 'Neues Ticket' : 'New Ticket'}
              </Button>
            </div>

            {/* Tickets List */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle>{language === 'de' ? 'Meine Tickets' : 'My Tickets'}</CardTitle>
                <CardDescription>
                  {language === 'de' 
                    ? 'Übersicht aller Ihrer Support-Anfragen'
                    : 'Overview of all your support requests'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                    {language === 'de' ? 'Lädt...' : 'Loading...'}
                  </div>
                ) : tickets.length === 0 ? (
                  <div className="text-center py-12">
                    <HelpCircle className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      {language === 'de' 
                        ? 'Noch keine Tickets erstellt'
                        : 'No tickets created yet'}
                    </p>
                    <Button 
                      variant="outline" 
                      className="mt-4"
                      onClick={() => setShowNewTicketModal(true)}
                    >
                      {language === 'de' ? 'Erstes Ticket erstellen' : 'Create first ticket'}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {tickets.map((ticket) => (
                      <div 
                        key={ticket.ticket_id}
                        onClick={() => fetchTicketDetails(ticket.ticket_id)}
                        className="p-4 rounded-lg bg-zinc-800/50 hover:bg-zinc-800 transition-colors cursor-pointer border border-zinc-700/50"
                        data-testid={`ticket-${ticket.ticket_id}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs font-mono text-emerald-400">{ticket.ticket_number}</span>
                              {getCategoryBadge(ticket.category)}
                              {getStatusBadge(ticket.status)}
                            </div>
                            <h3 className="font-medium text-white truncate">{ticket.title}</h3>
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{ticket.description}</p>
                          </div>
                          <div className="text-right text-xs text-muted-foreground whitespace-nowrap">
                            {formatDate(ticket.created_at)}
                          </div>
                        </div>
                        {getAttachmentCount(ticket) > 0 && (
                          <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
                            <Paperclip className="h-3 w-3" />
                            {getAttachmentCount(ticket)} {language === 'de' ? 'Anhang' : 'attachment'}{getAttachmentCount(ticket) > 1 ? (language === 'de' ? 'änge' : 's') : ''}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </main>

        {/* New Ticket Modal */}
        <Dialog open={showNewTicketModal} onOpenChange={setShowNewTicketModal}>
          <DialogContent className="glass max-w-lg">
            <DialogHeader>
              <DialogTitle>{language === 'de' ? 'Neues Ticket erstellen' : 'Create New Ticket'}</DialogTitle>
              <DialogDescription>
                {ticketForm.category === 'bug'
                  ? (language === 'de' 
                      ? 'Beschreiben Sie das Problem so detailliert wie möglich'
                      : 'Describe the issue as detailed as possible')
                  : (language === 'de'
                      ? 'Beschreiben Sie Ihre Feature-Idee so detailliert wie möglich'
                      : 'Describe your feature idea as detailed as possible')
                }
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label className="mb-3 block">{language === 'de' ? 'Kategorie' : 'Category'}</Label>
                <RadioGroup 
                  value={ticketForm.category} 
                  onValueChange={(value) => setTicketForm(prev => ({ ...prev, category: value }))}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2 p-3 rounded-lg border border-zinc-700 hover:border-rose-500/50 cursor-pointer flex-1">
                    <RadioGroupItem value="bug" id="bug" />
                    <Label htmlFor="bug" className="flex items-center gap-2 cursor-pointer flex-1">
                      <Bug className="h-4 w-4 text-rose-400" />
                      Bug
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 p-3 rounded-lg border border-zinc-700 hover:border-purple-500/50 cursor-pointer flex-1">
                    <RadioGroupItem value="feature" id="feature" />
                    <Label htmlFor="feature" className="flex items-center gap-2 cursor-pointer flex-1">
                      <Lightbulb className="h-4 w-4 text-purple-400" />
                      Feature
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label htmlFor="title">
                  {language === 'de' ? 'Titel' : 'Title'} <span className="text-red-500">*</span>
                </Label>
                <Input 
                  id="title"
                  value={ticketForm.title}
                  onChange={(e) => setTicketForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder={ticketForm.category === 'bug' 
                    ? (language === 'de' ? 'Kurze Beschreibung des Problems' : 'Brief description of the issue')
                    : (language === 'de' ? 'Kurze Beschreibung Ihrer Idee' : 'Brief description of your idea')
                  }
                  className={`mt-1 ${!ticketForm.title.trim() && ticketForm.description.trim() ? 'border-red-500/50' : ''}`}
                  data-testid="ticket-title-input"
                />
                {!ticketForm.title.trim() && ticketForm.description.trim() && (
                  <p className="text-xs text-red-400 mt-1">
                    {language === 'de' ? 'Bitte geben Sie einen Titel ein' : 'Please enter a title'}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="description">
                  {language === 'de' ? 'Beschreibung' : 'Description'} <span className="text-red-500">*</span>
                </Label>
                <Textarea 
                  id="description"
                  value={ticketForm.description}
                  onChange={(e) => setTicketForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder={ticketForm.category === 'bug'
                    ? (language === 'de' 
                        ? 'Beschreiben Sie das Problem im Detail. Was haben Sie erwartet? Was ist stattdessen passiert?'
                        : 'Describe the issue in detail. What did you expect? What happened instead?')
                    : (language === 'de'
                        ? 'Beschreiben Sie Ihre Feature-Idee im Detail. Welches Problem würde es lösen? Wie stellen Sie sich die Umsetzung vor?'
                        : 'Describe your feature idea in detail. What problem would it solve? How do you envision it?')
                  }
                  className="mt-1 min-h-[120px]"
                  data-testid="ticket-description-input"
                />
              </div>

              {/* Attachments Section */}
              <div>
                <Label className="mb-2 block">
                  {language === 'de' ? 'Anhänge (optional)' : 'Attachments (optional)'}
                </Label>
                <p className="text-xs text-muted-foreground mb-3">
                  {language === 'de' 
                    ? 'Screenshots oder CSV-Dateien (z.B. für neue Broker-Formate)'
                    : 'Screenshots or CSV files (e.g. for new broker formats)'}
                </p>
                
                {/* Attachment previews */}
                {ticketForm.attachments.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {ticketForm.attachments.map((att, index) => (
                      <div key={index} className="flex items-center gap-3 p-2 rounded-lg bg-zinc-800/50 border border-zinc-700">
                        {att.type === 'image' ? (
                          <>
                            <img 
                              src={att.preview} 
                              alt={att.filename}
                              className="h-10 w-10 object-cover rounded"
                            />
                            <Image className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                          </>
                        ) : (
                          <div className="h-10 w-10 flex items-center justify-center bg-emerald-500/20 rounded">
                            <FileSpreadsheet className="h-5 w-5 text-emerald-400" />
                          </div>
                        )}
                        <span className="flex-1 text-sm truncate">{att.filename}</span>
                        <button
                          onClick={() => removeAttachment(index)}
                          className="p-1 rounded hover:bg-zinc-700 text-zinc-400 hover:text-white"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Upload button */}
                <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-zinc-700 rounded-lg cursor-pointer hover:border-emerald-500/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <Upload className="h-5 w-5 text-muted-foreground" />
                    <div className="text-center">
                      <span className="text-sm text-muted-foreground">
                        {language === 'de' ? 'Klicken zum Hochladen' : 'Click to upload'}
                      </span>
                      <p className="text-xs text-zinc-500 mt-1">
                        {language === 'de' ? 'Bilder oder CSV-Dateien' : 'Images or CSV files'}
                      </p>
                    </div>
                  </div>
                  <input 
                    type="file" 
                    accept="image/*,.csv,text/csv" 
                    multiple
                    className="hidden" 
                    onChange={handleFileUpload}
                    data-testid="attachment-upload-input"
                  />
                </label>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewTicketModal(false)}>
                {language === 'de' ? 'Abbrechen' : 'Cancel'}
              </Button>
              <Button 
                onClick={handleSubmitTicket} 
                disabled={submitting || !ticketForm.title.trim() || !ticketForm.description.trim()}
                className="glow-primary"
                data-testid="submit-ticket-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {language === 'de' ? 'Wird gesendet...' : 'Submitting...'}
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    {language === 'de' ? 'Absenden' : 'Submit'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Ticket Detail Modal */}
        <Dialog open={showTicketDetailModal} onOpenChange={setShowTicketDetailModal}>
          <DialogContent className="glass max-w-2xl max-h-[80vh] overflow-y-auto">
            {selectedTicket && (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-mono text-emerald-400">{selectedTicket.ticket_number}</span>
                    {getCategoryBadge(selectedTicket.category)}
                    {getStatusBadge(selectedTicket.status)}
                  </div>
                  <DialogTitle>{selectedTicket.title}</DialogTitle>
                  <DialogDescription>
                    {language === 'de' ? 'Erstellt am' : 'Created on'} {formatDate(selectedTicket.created_at)}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                  {/* Description */}
                  <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                    <p className="text-sm whitespace-pre-wrap">{selectedTicket.description}</p>
                  </div>

                  {/* Attachments */}
                  {(selectedTicket.attachments?.length > 0 || selectedTicket.screenshot_url) && (
                    <div>
                      <Label className="mb-2 block flex items-center gap-2">
                        <Paperclip className="h-4 w-4" />
                        {language === 'de' ? 'Anhänge' : 'Attachments'}
                      </Label>
                      <div className="space-y-3">
                        {/* New attachments format */}
                        {selectedTicket.attachments?.map((att, index) => (
                          <div key={index} className="rounded-lg border border-zinc-700 overflow-hidden">
                            {att.type === 'image' ? (
                              <img 
                                src={att.data_url} 
                                alt={att.filename}
                                className="w-full max-h-[300px] object-contain bg-zinc-900"
                              />
                            ) : (
                              <div className="flex items-center justify-between p-4 bg-zinc-800/50">
                                <div className="flex items-center gap-3">
                                  <div className="h-10 w-10 flex items-center justify-center bg-emerald-500/20 rounded">
                                    <FileSpreadsheet className="h-5 w-5 text-emerald-400" />
                                  </div>
                                  <div>
                                    <p className="font-medium">{att.filename}</p>
                                    <p className="text-xs text-muted-foreground">CSV-Datei</p>
                                  </div>
                                </div>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => downloadAttachment(att)}
                                >
                                  <Download className="h-4 w-4 mr-2" />
                                  {language === 'de' ? 'Herunterladen' : 'Download'}
                                </Button>
                              </div>
                            )}
                          </div>
                        ))}
                        
                        {/* Legacy screenshot_url support */}
                        {!selectedTicket.attachments?.length && selectedTicket.screenshot_url && (
                          <img 
                            src={selectedTicket.screenshot_url} 
                            alt="Screenshot" 
                            className="w-full rounded-lg border border-zinc-700"
                          />
                        )}
                      </div>
                    </div>
                  )}

                  {/* Comments */}
                  <div>
                    <Label className="mb-2 block flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      {language === 'de' ? 'Kommentare' : 'Comments'}
                    </Label>
                    <div className="space-y-3 max-h-[200px] overflow-y-auto">
                      {selectedTicket.comments?.length > 0 ? (
                        selectedTicket.comments.map((comment) => (
                          <div 
                            key={comment.comment_id}
                            className={`p-3 rounded-lg ${
                              comment.is_admin 
                                ? 'bg-emerald-500/10 border border-emerald-500/30' 
                                : 'bg-zinc-800/50 border border-zinc-700'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className={`text-xs font-medium ${comment.is_admin ? 'text-emerald-400' : 'text-muted-foreground'}`}>
                                {comment.user_name}
                                {comment.is_admin && ' (Support)'}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {formatDate(comment.created_at)}
                              </span>
                            </div>
                            <p className="text-sm">{comment.text}</p>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          {language === 'de' ? 'Noch keine Kommentare' : 'No comments yet'}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Add Comment */}
                  {selectedTicket.status !== 'done' && (
                    <div className="flex gap-2">
                      <Input 
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder={language === 'de' ? 'Kommentar hinzufügen...' : 'Add a comment...'}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                      />
                      <Button onClick={handleAddComment} disabled={!newComment.trim()}>
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};

export default SupportPage;
